import { useAuth } from '../contexts/AuthContext'
import { useMemo, useState, useEffect } from 'react'
import { themePresets } from '../utils/themePresets'

export interface ThemeColors {
  // Core palette
  primary: string
  primaryHover: string
  primaryLight: string
  primaryDark: string
  primaryAlpha5: string
  primaryAlpha10: string
  primaryAlpha20: string
  primaryAlpha40: string
  // Smart text color - always readable on dark backgrounds
  // Uses primary if it's light enough, otherwise falls back to white/cyan
  primaryText: string

  // Background tokens
  bgDeep: string              // Main background (#080a0f)
  bgSurface: string           // Cards, elevated surfaces
  bgCard: string              // Alternative card bg
  background: string          // Alias for bgDeep (backwards compat)
  backgroundLight: string     // Alias for bgSurface
  backgroundDark: string
  surface: string             // Alias for bgCard

  // Accent
  accent: string
  accentDim: string           // Backgrounds, subtle emphasis
  accentGlow: string          // Glow effects

  // Text
  text: string
  textPrimary: string         // Main text (#f8fafc)
  textSecondary: string       // Muted text, labels (#94a3b8)
  textMuted: string           // Disabled, hints

  // Borders
  border: string
  borderLight: string         // Subtle dividers
  borderDefault: string       // Card borders
  borderHover: string         // Hover emphasis

  // Status colors
  success: string
  successBg: string
  successBorder: string
  successAlpha10: string
  warning: string
  warningBg: string
  warningBorder: string
  warningAlpha10: string
  error: string
  errorBg: string
  errorBorder: string
  info: string
  infoBg: string
  infoBorder: string

  // Glassmorphism tokens
  glass: {
    background: string
    border: string
    backdropBlur: string
    hoverBorder: string
    hoverShadow: string
    hoverTransform: string
    sidebar: string
    header: string
    elevated: string
  }

  // Elevation/shadows
  elevation: {
    level1: string            // Subtle depth (cards at rest)
    level2: string            // Hover state (raised cards)
    level3: string            // Modals, dropdowns
    glowCyan: string          // Accent glow
    glowCyanStrong: string    // Strong accent glow
  }

  // Spacing scale
  spacing: {
    micro: string             // 4px
    xs: string                // 8px
    sm: string                // 12px
    md: string                // 16px
    base: string              // 20px
    lg: string                // 24px
    xl: string                // 32px
    '2xl': string             // 40px
    '3xl': string             // 48px
    '4xl': string             // 64px
  }

  // Typography
  fonts: {
    heading: string           // Space Grotesk
    body: string              // Outfit
    display: string           // Space Grotesk (alias)
  }
  fontSize: {
    xs: string                // 12px
    sm: string                // 13px
    base: string              // 14px
    md: string                // 16px
    lg: string                // 18px
    xl: string                // 20px
    '2xl': string             // 24px
    '3xl': string             // 32px
    '4xl': string             // 48px
    h1: string                // Alias for 2xl
    h2: string                // Alias for xl
    bodyLarge: string         // Alias for md
    bodySmall: string         // Alias for base
    micro: string             // Alias for xs
  }

  // Border radius scale
  borderRadius: {
    micro: string             // 4px
    sm: string                // 8px
    pills: string             // 12px
    input: string             // 14px
    card: string              // 16px
    lg: string                // 20px
    xl: string                // 24px
    full: string              // 100px
  }

  // Motion
  motion: {
    fast: string              // 150ms
    normal: string            // 200ms
    slow: string              // 300ms
    slower: string            // 400ms
    easeStandard: string
    easeDecelerate: string
    easeAccelerate: string
    easeBounce: string
  }
}

interface RGBAColor {
  r: number
  g: number
  b: number
  a?: number
}

// Helper function to convert hex to RGBA
function hexToRgba(hex: string, alpha: number = 1): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return hex
  
  const r = parseInt(result[1], 16)
  const g = parseInt(result[2], 16)
  const b = parseInt(result[3], 16)
  
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// Helper function to adjust color brightness
function adjustBrightness(color: string, percent: number): string {
  const num = parseInt(color.replace('#', ''), 16)
  const amt = Math.round(2.55 * percent)
  const R = (num >> 16) + amt
  const G = (num >> 8 & 0x00FF) + amt
  const B = (num & 0x0000FF) + amt
  
  return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255))
    .toString(16)
    .slice(1)
}

// Helper function to determine if a color is light or dark
function isLightColor(color: string): boolean {
  // Convert hex to RGB
  const hex = color.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5
}

export function useThemeColors(): { colors: ThemeColors; isLightTheme: boolean } {
  const { isSuperAdmin } = useAuth()

  // Read org primary color from CSS variable (set by BrandingContext)
  // Use state + effect to re-read when CSS variables change
  const [orgPrimary, setOrgPrimary] = useState(() => {
    if (typeof window === 'undefined') return '#00f2ff'
    const value = getComputedStyle(document.documentElement).getPropertyValue('--org-primary').trim()
    return value || '#00f2ff'
  })

  // Re-read CSS variable periodically to catch updates from BrandingContext
  useEffect(() => {
    const checkCssVar = () => {
      const value = getComputedStyle(document.documentElement).getPropertyValue('--org-primary').trim()
      if (value && value !== orgPrimary) {
        setOrgPrimary(value)
      }
    }
    // Check immediately
    checkCssVar()
    // Then check periodically (BrandingContext sets vars after async fetch)
    const interval = setInterval(checkCssVar, 100)
    // Also check on DOM changes
    const observer = new MutationObserver(checkCssVar)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] })

    return () => {
      clearInterval(interval)
      observer.disconnect()
    }
  }, [orgPrimary])

  return useMemo(() => {
    // Get computed styles to read CSS variables (set by BrandingContext)
    const root = typeof window !== 'undefined' ? document.documentElement : null
    const getVar = (name: string, fallback: string) => {
      if (!root) return fallback
      const value = getComputedStyle(root).getPropertyValue(name).trim()
      return value || fallback
    }

    // Check if super admin is in platform mode (uses pastel blue theme)
    const isInPlatformMode = isSuperAdmin && localStorage.getItem('super-admin-mode') === 'platform'

    if (isInPlatformMode) {
      const platformColors: ThemeColors = {
        // Core palette
        primary: '#6B9BD1',
        primaryHover: '#5A89C0',
        primaryLight: '#A8C5E4',
        primaryDark: '#4A7AAF',
        primaryAlpha5: 'rgba(107, 155, 209, 0.05)',
        primaryAlpha10: 'rgba(107, 155, 209, 0.1)',
        primaryAlpha20: 'rgba(107, 155, 209, 0.2)',
        primaryAlpha40: 'rgba(107, 155, 209, 0.4)',
        // Platform blue is light enough for text
        primaryText: '#6B9BD1',

        // Background tokens
        bgDeep: '#080a0f',
        bgSurface: 'rgba(255, 255, 255, 0.03)',
        bgCard: 'rgba(15, 18, 25, 0.8)',
        background: '#080a0f',
        backgroundLight: 'rgba(255, 255, 255, 0.03)',
        backgroundDark: '#050709',
        surface: 'rgba(15, 18, 25, 0.8)',

        // Accent
        accent: '#6B9BD1',
        accentDim: 'rgba(107, 155, 209, 0.1)',
        accentGlow: 'rgba(107, 155, 209, 0.2)',

        // Text
        text: '#f8fafc',
        textPrimary: '#f8fafc',
        textSecondary: '#94a3b8',
        textMuted: 'rgba(255, 255, 255, 0.5)',

        // Borders
        border: 'rgba(255, 255, 255, 0.08)',
        borderLight: 'rgba(255, 255, 255, 0.05)',
        borderDefault: 'rgba(255, 255, 255, 0.08)',
        borderHover: 'rgba(107, 155, 209, 0.3)',

        // Status colors
        success: '#10b981',
        successBg: 'rgba(16, 185, 129, 0.15)',
        successBorder: 'rgba(16, 185, 129, 0.3)',
        successAlpha10: 'rgba(16, 185, 129, 0.1)',
        warning: '#f59e0b',
        warningBg: 'rgba(245, 158, 11, 0.15)',
        warningBorder: 'rgba(245, 158, 11, 0.3)',
        warningAlpha10: 'rgba(245, 158, 11, 0.1)',
        error: '#ef4444',
        errorBg: 'rgba(239, 68, 68, 0.15)',
        errorBorder: 'rgba(239, 68, 68, 0.3)',
        info: '#6B9BD1',
        infoBg: 'rgba(107, 155, 209, 0.15)',
        infoBorder: 'rgba(107, 155, 209, 0.3)',

        // Glassmorphism
        glass: {
          background: 'rgba(255, 255, 255, 0.03)',
          border: 'rgba(255, 255, 255, 0.08)',
          backdropBlur: '12px',
          hoverBorder: 'rgba(107, 155, 209, 0.3)',
          hoverShadow: '0 20px 40px rgba(107, 155, 209, 0.1)',
          hoverTransform: 'translateY(-4px)',
          sidebar: 'rgba(8, 10, 15, 0.8)',
          header: 'rgba(8, 10, 15, 0.9)',
          elevated: 'rgba(15, 18, 25, 0.8)'
        },

        // Elevation
        elevation: {
          level1: '0 2px 8px rgba(0, 0, 0, 0.2)',
          level2: '0 10px 30px rgba(0, 0, 0, 0.3)',
          level3: '0 20px 40px rgba(0, 0, 0, 0.4)',
          glowCyan: '0 0 15px rgba(107, 155, 209, 0.2)',
          glowCyanStrong: '0 0 25px rgba(107, 155, 209, 0.4)'
        },

        // Spacing
        spacing: {
          micro: '4px',
          xs: '8px',
          sm: '12px',
          md: '16px',
          base: '20px',
          lg: '24px',
          xl: '32px',
          '2xl': '40px',
          '3xl': '48px',
          '4xl': '64px'
        },

        // Typography
        fonts: {
          heading: '"Space Grotesk", sans-serif',
          body: '"Outfit", sans-serif',
          display: '"Space Grotesk", sans-serif'
        },
        fontSize: {
          xs: '12px',
          sm: '13px',
          base: '14px',
          md: '16px',
          lg: '18px',
          xl: '20px',
          '2xl': '24px',
          '3xl': '32px',
          '4xl': '48px',
          h1: '24px',
          h2: '20px',
          bodyLarge: '16px',
          bodySmall: '14px',
          micro: '12px'
        },

        // Border radius
        borderRadius: {
          micro: '4px',
          sm: '8px',
          pills: '12px',
          input: '14px',
          card: '16px',
          lg: '20px',
          xl: '24px',
          full: '100px'
        },

        // Motion
        motion: {
          fast: '150ms',
          normal: '200ms',
          slow: '300ms',
          slower: '400ms',
          easeStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
          easeDecelerate: 'cubic-bezier(0, 0, 0.2, 1)',
          easeAccelerate: 'cubic-bezier(0.4, 0, 1, 1)',
          easeBounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
        }
      }
      return { colors: platformColors, isLightTheme: false }
    }

    // Use org colors from CSS variables (set by BrandingContext)
    // Falls back to AIFM brand colors (cyan theme) if not set
    const aifmPreset = themePresets['aifm-brand']

    // Calculate a readable text color for dark backgrounds
    // If primary is too dark (low luminance), use white or the default cyan instead
    const isPrimaryReadable = isLightColor(orgPrimary)
    const safeTextColor = isPrimaryReadable ? orgPrimary : '#00f2ff'

    const orgColors: ThemeColors = {
      // Core palette - use org color from CSS variable
      primary: orgPrimary,
      primaryHover: getVar('--org-primary-dark', adjustBrightness(orgPrimary, -15)),
      primaryLight: getVar('--org-primary-light', adjustBrightness(orgPrimary, 15)),
      primaryDark: getVar('--org-primary-dark', adjustBrightness(orgPrimary, -15)),
      primaryAlpha5: hexToRgba(orgPrimary, 0.05),
      primaryAlpha10: hexToRgba(orgPrimary, 0.1),
      primaryAlpha20: hexToRgba(orgPrimary, 0.2),
      primaryAlpha40: hexToRgba(orgPrimary, 0.4),
      // Smart text color - readable on dark backgrounds
      primaryText: safeTextColor,

      // Background tokens - keep dark theme
      bgDeep: '#080a0f',
      bgSurface: 'rgba(255, 255, 255, 0.03)',
      bgCard: 'rgba(15, 18, 25, 0.8)',
      background: aifmPreset.colors.background,
      backgroundLight: aifmPreset.colors.backgroundLight,
      backgroundDark: aifmPreset.colors.backgroundDark,
      surface: aifmPreset.colors.surface,

      // Accent - use org primary
      accent: orgPrimary,
      accentDim: hexToRgba(orgPrimary, 0.1),
      accentGlow: hexToRgba(orgPrimary, 0.2),

      // Text
      text: aifmPreset.colors.text,
      textPrimary: '#f8fafc',
      textSecondary: '#94a3b8',
      textMuted: aifmPreset.colors.textMuted,

      // Borders - update hover to use org primary
      border: aifmPreset.colors.border,
      borderLight: aifmPreset.colors.borderLight,
      borderDefault: 'rgba(255, 255, 255, 0.08)',
      borderHover: hexToRgba(orgPrimary, 0.3),

      // Status colors
      success: '#10b981',
      successBg: 'rgba(16, 185, 129, 0.15)',
      successBorder: 'rgba(16, 185, 129, 0.3)',
      successAlpha10: hexToRgba('#10b981', 0.1),
      warning: '#f59e0b',
      warningBg: 'rgba(245, 158, 11, 0.15)',
      warningBorder: 'rgba(245, 158, 11, 0.3)',
      warningAlpha10: hexToRgba('#f59e0b', 0.1),
      error: '#ef4444',
      errorBg: 'rgba(239, 68, 68, 0.15)',
      errorBorder: 'rgba(239, 68, 68, 0.3)',
      info: orgPrimary,
      infoBg: hexToRgba(orgPrimary, 0.15),
      infoBorder: hexToRgba(orgPrimary, 0.3),

      // Glassmorphism - update hover effects to use org primary
      glass: {
        background: 'rgba(255, 255, 255, 0.03)',
        border: 'rgba(255, 255, 255, 0.08)',
        backdropBlur: '12px',
        hoverBorder: hexToRgba(orgPrimary, 0.3),
        hoverShadow: `0 20px 40px ${hexToRgba(orgPrimary, 0.1)}`,
        hoverTransform: 'translateY(-4px)',
        sidebar: 'rgba(8, 10, 15, 0.8)',
        header: 'rgba(8, 10, 15, 0.9)',
        elevated: 'rgba(15, 18, 25, 0.8)'
      },

      // Elevation/shadows - update glow colors
      elevation: {
        level1: '0 2px 8px rgba(0, 0, 0, 0.2)',
        level2: '0 10px 30px rgba(0, 0, 0, 0.3)',
        level3: '0 20px 40px rgba(0, 0, 0, 0.4)',
        glowCyan: `0 0 15px ${hexToRgba(orgPrimary, 0.2)}`,
        glowCyanStrong: `0 0 25px ${hexToRgba(orgPrimary, 0.4)}`
      },

      // Spacing scale (DESIGN_SYSTEM.md)
      spacing: {
        micro: '4px',
        xs: '8px',
        sm: '12px',
        md: '16px',
        base: '20px',
        lg: '24px',
        xl: '32px',
        '2xl': '40px',
        '3xl': '48px',
        '4xl': '64px'
      },

      // Typography (DESIGN_SYSTEM.md)
      fonts: {
        heading: '"Space Grotesk", sans-serif',
        body: '"Outfit", sans-serif',
        display: '"Space Grotesk", sans-serif'
      },
      fontSize: {
        xs: '12px',
        sm: '13px',
        base: '14px',
        md: '16px',
        lg: '18px',
        xl: '20px',
        '2xl': '24px',
        '3xl': '32px',
        '4xl': '48px',
        h1: '24px',
        h2: '20px',
        bodyLarge: '16px',
        bodySmall: '14px',
        micro: '12px'
      },

      // Border radius scale (DESIGN_SYSTEM.md)
      borderRadius: {
        micro: '4px',
        sm: '8px',
        pills: '12px',
        input: '14px',
        card: '16px',
        lg: '20px',
        xl: '24px',
        full: '100px'
      },

      // Motion (DESIGN_SYSTEM.md)
      motion: {
        fast: '150ms',
        normal: '200ms',
        slow: '300ms',
        slower: '400ms',
        easeStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeDecelerate: 'cubic-bezier(0, 0, 0.2, 1)',
        easeAccelerate: 'cubic-bezier(0.4, 0, 1, 1)',
        easeBounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
      }
    }

    return { colors: orgColors, isLightTheme: false }
  }, [isSuperAdmin, orgPrimary])
}

// Hook to get specific color values with opacity
export function useThemeColor(colorName: keyof ThemeColors, opacity?: number): string {
  const { colors } = useThemeColors()
  const color = colors[colorName]
  
  if (opacity !== undefined && opacity !== 1) {
    return hexToRgba(color, opacity)
  }
  
  return color
}

// Hook to get Tailwind-compatible color classes
export function useThemeClasses() {
  const { colors } = useThemeColors()
  
  return {
    // Background classes
    bgPrimary: `bg-[${colors.primary}]`,
    bgPrimaryHover: `hover:bg-[${colors.primaryHover}]`,
    bgBackground: `bg-[${colors.background}]`,
    bgBackgroundLight: `bg-[${colors.backgroundLight}]`,
    bgBackgroundDark: `bg-[${colors.backgroundDark}]`,
    
    // Text classes
    textPrimary: `text-[${colors.primary}]`,
    textPrimaryHover: `hover:text-[${colors.primary}]`,
    textDefault: `text-[${colors.text}]`,
    textSecondary: `text-[${colors.textSecondary}]`,
    textMuted: `text-[${colors.textMuted}]`,
    
    // Border classes
    borderPrimary: `border-[${colors.primary}]`,
    borderDefault: `border-[${colors.border}]`,
    borderLight: `border-[${colors.borderLight}]`,
    
    // Button classes
    btnPrimary: `bg-[${colors.primary}] text-[${colors.background}] hover:bg-[${colors.primaryHover}]`,
    btnSecondary: `bg-transparent border-2 border-[${colors.primary}] text-[${colors.primary}] hover:bg-[${colors.primary}] hover:text-[${colors.background}]`,
    
    // Status classes
    bgSuccess: `bg-[${colors.success}]`,
    bgError: `bg-[${colors.error}]`,
    bgWarning: `bg-[${colors.warning}]`,
    bgInfo: `bg-[${colors.info}]`
  }
}

// Export utility functions for use in other components
export { hexToRgba, adjustBrightness }
